import { useState, useEffect, useRef } from 'react';
import { MapPin, CheckCircle2, AlertTriangle, Loader2, Bell } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Props {
  sessionId: string;
  apiBase: string;
  onSuccess: () => void;
  onSkip?: () => void;   // Fora do perimetro → exige Prova de Vida
  onError: (msg: string) => void;
}

interface GeoResult {
  requireLiveness: boolean;
  distanciaMetros: number | null;
  escolaLat: number | null;
  escolaLng: number | null;
  raioM: number;
}

/**
 * GeofenceChecker — Verifica localizacao GPS e exibe mapa interativo.
 * Dentro do perimetro: circulo verde, pula liveness.
 * Fora: circulo vermelho, exige Prova de Vida.
 */
export function GeofenceChecker({ sessionId, apiBase, onSuccess, onSkip, onError }: Props) {
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<GeoResult | null>(null);
  const [userPos, setUserPos] = useState<{ lat: number; lng: number } | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [animDone, setAnimDone] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  // Verificar silenciosamente se a escola tem localização cadastrada no banco
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/security/session/${sessionId}/geofence`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lat: null, lng: null }),
        });
        if (res.ok) {
          const data: GeoResult = await res.json();
          if (!data.escolaLat || !data.escolaLng) {
            // Sem coordenadas cadastradas -> PULA O PASSO IMEDIATAMENTE
            console.log('[Geofence] Escola sem coordenadas. Pulando etapa...');
            onSkip?.();
          }
        }
      } catch (err) {
        console.error('[Geofence] Falha ao verificar coordenadas da escola:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verificar = async () => {
    setLoading(true);
    try {
      const pos: GeolocationPosition = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true, timeout: 10000, maximumAge: 0,
        });
      });

      const { latitude: lat, longitude: lng } = pos.coords;
      setUserPos({ lat, lng });

      const res = await fetch(`${apiBase}/security/session/${sessionId}/geofence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat, lng }),
      });

      if (!res.ok) throw new Error('Erro ao verificar localizacao');
      const data: GeoResult = await res.json();
      setResultado(data);
      // Se a escola nao tem coordenadas, pular mapa e ir direto para botao
      if (!data.escolaLat || !data.escolaLng) {
        setAnimDone(true);
      }
      setShowMap(true);
    } catch (e: any) {
      console.warn('[Geofence] Falha ao obter GPS. Pulando geofence:', e);
      if (onSkip) {
        onSkip();
      } else {
        onError('Nao foi possivel verificar sua localizacao.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Renderizar mapa quando resultado chegar
  useEffect(() => {
    if (!showMap || !resultado || !userPos || !resultado.escolaLat || !resultado.escolaLng) return;
    if (!mapRef.current || mapInstanceRef.current) return;

    const escolaPos: L.LatLngExpression = [resultado.escolaLat, resultado.escolaLng];
    const userLatLng: L.LatLngExpression = [userPos.lat, userPos.lng];
    const isDentro = !resultado.requireLiveness;

    // Criar mapa centrado na escola
    const map = L.map(mapRef.current, {
      center: escolaPos,
      zoom: 16,
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
    });
    mapInstanceRef.current = map;

    // Tiles dark
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
    }).addTo(map);

    // Circulo do perimetro escolar
    const circleColor = isDentro ? '#10b981' : '#ef4444';
    L.circle(escolaPos, {
      radius: resultado.raioM,
      color: circleColor,
      fillColor: circleColor,
      fillOpacity: 0.12,
      weight: 2,
    }).addTo(map);

    // Marcador da escola
    const escolaIcon = L.divIcon({
      html: `<div style="width:32px;height:32px;border-radius:50%;background:${circleColor};display:flex;align-items:center;justify-content:center;border:3px solid rgba(255,255,255,0.8);box-shadow:0 2px 10px rgba(0,0,0,0.5);">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><path d="M2 22h20"/><path d="M6 18V7"/><path d="M18 18V7"/><path d="M2 11l10-7 10 7"/><rect x="10" y="12" width="4" height="6"/></svg>
      </div>`,
      className: '',
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
    L.marker(escolaPos, { icon: escolaIcon }).addTo(map);

    // Animacao: apos 500ms, animar para mostrar o usuario
    setTimeout(() => {
      // Marcador do usuario
      const userIcon = L.divIcon({
        html: `<div style="width:28px;height:28px;border-radius:50%;background:#3b82f6;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 10px rgba(0,0,0,0.5);">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 1 0-16 0"/></svg>
        </div>`,
        className: '',
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });
      L.marker(userLatLng, { icon: userIcon }).addTo(map);

      // Linha curva (arco) da escola ate o usuario
      const midLat = (resultado.escolaLat! + userPos.lat) / 2;
      const midLng = (resultado.escolaLng! + userPos.lng) / 2;
      // Offset perpendicular para criar curvatura
      const dLat = userPos.lat - resultado.escolaLat!;
      const dLng = userPos.lng - resultado.escolaLng!;
      const offset = Math.max(Math.abs(dLat), Math.abs(dLng)) * 0.3;
      const ctrlLat = midLat + (dLng * 0.5);
      const ctrlLng = midLng - (dLat * 0.5);

      // Gerar pontos da curva de Bezier
      const arcPoints: L.LatLngExpression[] = [];
      const steps = 30;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const lat = (1-t)*(1-t)*resultado.escolaLat! + 2*(1-t)*t*ctrlLat + t*t*userPos.lat;
        const lng = (1-t)*(1-t)*resultado.escolaLng! + 2*(1-t)*t*ctrlLng + t*t*userPos.lng;
        arcPoints.push([lat, lng]);
      }

      L.polyline(arcPoints, {
        color: circleColor,
        weight: 2.5,
        opacity: 0.7,
        dashArray: '8, 6',
      }).addTo(map);

      // Ajustar zoom para mostrar ambos os pontos
      const bounds = L.latLngBounds([escolaPos, userLatLng]);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16, animate: true, duration: 1.5 });

      setTimeout(() => setAnimDone(true), 1600);
    }, 800);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [showMap, resultado, userPos]);

  // Formatar distancia
  const formatDist = (m: number) => {
    if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
    return `${Math.round(m)} metros`;
  };

  // ── ESTADO INICIAL: botao de verificar ──
  if (!showMap) {
    return (
      <div className="glass-card animate-fade-in-up" style={{
        textAlign: 'center', padding: '2.5rem 1.5rem', borderRadius: 24,
        position: 'relative', overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
          width: 150, height: 150, background: 'rgba(234, 179, 8, 0.1)',
          filter: 'blur(40px)', zIndex: 0
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', margin: '0 auto 1.5rem',
            background: 'linear-gradient(135deg, rgba(234,179,8,0.15), rgba(202,138,4,0.05))',
            border: '1px solid rgba(234,179,8,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(234,179,8,0.1)'
          }}>
            <MapPin size={36} strokeWidth={1.5} style={{ color: '#eab308' }} />
          </div>

          <h2 style={{
            fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.5rem',
            color: '#f8fafc', letterSpacing: '-0.02em'
          }}>
            Verificacao de Localizacao
          </h2>
          <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '2.5rem', lineHeight: 1.6 }}>
            Precisamos verificar se voce esta proximo a escola. Seus dados de GPS <strong>nao sao armazenados</strong>.
          </p>

          <button
            onClick={verificar}
            disabled={loading}
            className={loading ? '' : 'premium-btn'}
            style={{
              width: '100%', padding: '1.1rem 1rem', borderRadius: 16, border: 'none',
              background: loading ? 'rgba(255,255,255,0.05)' : undefined,
              color: loading ? '#94a3b8' : undefined,
              fontSize: '1rem', fontWeight: 700,
              cursor: loading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}
          >
            {loading
              ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Verificando...</>
              : <>Verificar localizacao</>}
          </button>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── MAPA + RESULTADO ──
  const isDentro = resultado && !resultado.requireLiveness;
  const temCoordenadas = resultado?.escolaLat && resultado?.escolaLng;

  if (showMap && !temCoordenadas) {
    return (
      <div className="glass-card animate-fade-in-up" style={{ 
        textAlign: 'center', padding: '2.5rem 1.5rem', borderRadius: 24,
        position: 'relative', overflow: 'hidden'
      }}>
        {/* Glow background */}
        <div style={{
          position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
          width: 150, height: 150, background: 'rgba(234, 179, 8, 0.1)',
          filter: 'blur(40px)', zIndex: 0
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', margin: '0 auto 1.5rem',
            background: 'linear-gradient(135deg, rgba(234,179,8,0.15), rgba(202,138,4,0.05))',
            border: '1px solid rgba(234,179,8,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(234,179,8,0.1)'
          }}>
            <Bell size={36} strokeWidth={1.5} style={{ color: '#eab308' }} />
          </div>

          <h2 style={{ 
            fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.5rem', 
            color: '#f8fafc', letterSpacing: '-0.02em' 
          }}>
            Configuração de Segurança
          </h2>
          <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '2.5rem', lineHeight: 1.6 }}>
            Para garantir a proteção da sua conta e autorizar sessões em tempo real, ative as notificações push de segurança neste dispositivo.
          </p>

          <button
            onClick={onSkip}
            className="premium-btn"
            style={{
              width: '100%', padding: '1.1rem 1rem', borderRadius: 16, border: 'none',
              fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}
          >
            Ativar Notificações
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card animate-fade-in-up" style={{
      borderRadius: 24, overflow: 'hidden', position: 'relative',
    }}>
      {/* Mapa */}
      {resultado?.escolaLat && resultado?.escolaLng ? (
        <div
          ref={mapRef}
          style={{
            width: '100%', height: 280, background: '#1a1a2e',
            transition: 'opacity 0.5s',
          }}
        />
      ) : (
        <div style={{
          width: '100%', height: 160, background: 'rgba(255,255,255,0.02)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.05)', gap: 12
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <MapPin size={24} style={{ color: '#eab308' }} />
          </div>
          <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>
            Localização da escola não cadastrada no sistema
          </p>
        </div>
      )}

      {/* Info abaixo do mapa */}
      <div style={{ padding: '1.25rem 1.5rem' }}>
        {/* Status */}
        <div style={{
          padding: '12px 16px', borderRadius: 14, marginBottom: 14,
          background: isDentro ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
          border: `1px solid ${isDentro ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)'}`,
          color: isDentro ? '#6ee7b7' : '#fcd34d',
          fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {isDentro
            ? <><CheckCircle2 size={18} /> Voce esta dentro do perimetro escolar</>
            : <><AlertTriangle size={18} /> Voce esta fora do perimetro da escola</>}
        </div>

        {/* Distancia */}
        {resultado?.distanciaMetros !== null && resultado?.distanciaMetros !== undefined && (
          <p style={{
            fontSize: '0.82rem', color: '#94a3b8', textAlign: 'center',
            margin: '0 0 16px',
          }}>
            Distancia ate a escola: <strong style={{ color: '#f8fafc' }}>
              {formatDist(resultado.distanciaMetros)}
            </strong>
          </p>
        )}

        {/* Botao condicional */}
        {animDone && (
          <button
            onClick={() => {
              if (isDentro) {
                onSuccess();
              } else {
                onSkip?.();
              }
            }}
            className="premium-btn"
            style={{
              width: '100%', padding: '1.1rem 1rem', borderRadius: 16, border: 'none',
              fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              animation: 'fade-in-up 0.4s ease-out',
            }}
          >
            {isDentro ? 'Entendi, continuar!' : 'Ativar Notificacoes'}
          </button>
        )}
      </div>
    </div>
  );
}
